import jsPDF from "jspdf";

import {
  computeUnitPrice,
  formatDisplayDate,
  formatEUR,
  getArticleMessage,
  getArticleName,
  toNumberSafe,
} from "./reglementsUtils";

const COLORS = {
  navy: [1, 62, 142],
  green: [76, 168, 47],
  slate900: [15, 23, 42],
  slate700: [51, 65, 85],
  slate500: [100, 116, 139],
  slate200: [226, 232, 240],
  slate100: [241, 245, 249],
  white: [255, 255, 255],
  emerald: [5, 150, 105],
};

const cleanFileName = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();

const getFullName = (user) => {
  return [user?.prenom, user?.nom].filter(Boolean).join(" ") || "Adhérent";
};

const getClubInfo = (settings = {}) => {
  return {
    name:
      settings?.nom_club ||
      settings?.club_name ||
      settings?.association_name ||
      settings?.nom_association ||
      settings?.structure_name ||
      "MonAppliClub",
    address: [
      settings?.adresse_club || settings?.adresse || settings?.address,
      settings?.code_postal || settings?.postal_code,
      settings?.ville || settings?.city,
    ]
      .filter(Boolean)
      .join(" "),
    email:
      settings?.email_club ||
      settings?.mail_club ||
      settings?.email ||
      settings?.mail ||
      "",
    phone:
      settings?.telephone_club ||
      settings?.tel_club ||
      settings?.telephone ||
      settings?.tel ||
      "",
    siret: settings?.siret || settings?.siret_club || "",
    website:
      settings?.site_web ||
      settings?.website ||
      settings?.url_site ||
      settings?.siteweb_club ||
      "",
    taxNote:
      settings?.invoice_tax_note ||
      settings?.tva_note ||
      "TVA non détaillée sur ce document.",
  };
};

const drawRoundedBlock = (doc, x, y, w, h, title, lines = []) => {
  doc.setDrawColor(...COLORS.slate200);
  doc.setFillColor(...COLORS.white);
  doc.roundedRect(x, y, w, h, 4, 4, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.navy);
  doc.text(title, x + 5, y + 8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.slate700);

  let cursorY = y + 15;

  lines.filter(Boolean).forEach((line) => {
    const wrapped = doc.splitTextToSize(String(line), w - 10);
    doc.text(wrapped, x + 5, cursorY);
    cursorY += wrapped.length * 5;
  });
};

const drawFooter = (doc) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setDrawColor(...COLORS.slate200);
  doc.line(16, pageHeight - 18, pageWidth - 16, pageHeight - 18);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.slate500);
  doc.text(
    "Document généré automatiquement depuis l’espace adhérent MonAppliClub.",
    16,
    pageHeight - 11
  );
};

const buildInvoiceNumber = (reglement) => {
  const date = reglement?.date_achat ? new Date(reglement.date_achat) : new Date();
  const year = Number.isNaN(date.getTime()) ? new Date().getFullYear() : date.getFullYear();

  return `FAC-${year}-${String(reglement?.id || "0000").padStart(5, "0")}`;
};

export const generateReglementInvoicePdf = ({ reglement, user, settings }) => {
  if (!reglement) {
    throw new Error("Aucun règlement fourni pour générer la facture.");
  }

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 16;

  const club = getClubInfo(settings);
  const invoiceNumber = buildInvoiceNumber(reglement);

  const articleName = getArticleName(reglement);
  const articleMessage = getArticleMessage(reglement);

  const quantity = Math.max(1, toNumberSafe(reglement?.quantite) || 1);
  const unitPrice = computeUnitPrice(reglement);
  const total = toNumberSafe(reglement?.prix_total) || unitPrice * quantity;

  const buyerName = getFullName(user);
  const buyerEmail = user?.email || user?.mail || "";
  const buyerAddress = [
    user?.adresse || user?.address,
    user?.code_postal || user?.postal_code,
    user?.ville || user?.city,
  ]
    .filter(Boolean)
    .join(" ");

  // Header
  doc.setFillColor(...COLORS.navy);
  doc.rect(0, 0, pageWidth, 34, "F");

  doc.setFillColor(...COLORS.green);
  doc.rect(0, 34, pageWidth, 2, "F");

  doc.setTextColor(...COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("FACTURE ACQUITTÉE", margin, 16);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`N° ${invoiceNumber}`, margin, 25);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(club.name, pageWidth - margin, 15, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Commande boutique réglée", pageWidth - margin, 24, {
    align: "right",
  });

  // Status pill
  doc.setFillColor(...COLORS.emerald);
  doc.roundedRect(margin, 44, 42, 10, 5, 5, "F");
  doc.setTextColor(...COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("RÉGLÉ", margin + 21, 50.5, { align: "center" });

  doc.setTextColor(...COLORS.slate700);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Date d’achat : ${formatDisplayDate(reglement?.date_achat)}`, margin, 64);
  doc.text(
    `Date d’émission : ${formatDisplayDate(new Date().toISOString())}`,
    margin,
    70
  );

  // Seller / buyer blocks
  drawRoundedBlock(doc, margin, 82, 86, 42, "Structure", [
    club.name,
    club.address,
    club.email ? `Email : ${club.email}` : "",
    club.phone ? `Téléphone : ${club.phone}` : "",
    club.siret ? `SIRET : ${club.siret}` : "",
    club.website ? `Site : ${club.website}` : "",
  ]);

  drawRoundedBlock(doc, margin + 92, 82, 86, 42, "Client / adhérent", [
    buyerName,
    buyerEmail ? `Email : ${buyerEmail}` : "",
    buyerAddress,
    user?.licence ? `Licence : ${user.licence}` : "",
  ]);

  // Payment details
  drawRoundedBlock(doc, margin, 132, pageWidth - margin * 2, 30, "Informations de paiement", [
    `Commande n° ${reglement?.id}`,
    `Statut : ${reglement?.statut_reglement || "Réglé"}`,
    `Mode de règlement : ${
      reglement?.mode_reglement || reglement?.type_reglement || "Paiement en ligne"
    }`,
    reglement?.stripe_session_id
      ? `Référence paiement : ${reglement.stripe_session_id}`
      : "",
  ]);

  // Table
  const tableY = 174;
  const tableX = margin;
  const tableW = pageWidth - margin * 2;

  doc.setFillColor(...COLORS.slate100);
  doc.setDrawColor(...COLORS.slate200);
  doc.roundedRect(tableX, tableY, tableW, 12, 3, 3, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.slate700);
  doc.text("Désignation", tableX + 5, tableY + 8);
  doc.text("Qté", tableX + 118, tableY + 8, { align: "right" });
  doc.text("Prix unitaire", tableX + 148, tableY + 8, { align: "right" });
  doc.text("Total", tableX + tableW - 5, tableY + 8, { align: "right" });

  const rowY = tableY + 12;
  doc.setFillColor(...COLORS.white);
  doc.rect(tableX, rowY, tableW, 28, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.slate900);

  const wrappedName = doc.splitTextToSize(articleName, 100);
  doc.text(wrappedName, tableX + 5, rowY + 8);

  if (articleMessage) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.slate500);
    const wrappedMessage = doc.splitTextToSize(articleMessage, 100);
    doc.text(wrappedMessage.slice(0, 2), tableX + 5, rowY + 16);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.slate900);
  doc.text(String(quantity), tableX + 118, rowY + 10, { align: "right" });
  doc.text(formatEUR(unitPrice), tableX + 148, rowY + 10, { align: "right" });
  doc.text(formatEUR(total), tableX + tableW - 5, rowY + 10, { align: "right" });

  // Totals
  const totalY = rowY + 40;

  doc.setDrawColor(...COLORS.slate200);
  doc.line(tableX + 98, totalY, tableX + tableW, totalY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.slate700);
  doc.text("Total", tableX + 120, totalY + 9);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.slate900);
  doc.text(formatEUR(total), tableX + tableW - 5, totalY + 9, {
    align: "right",
  });

  doc.setFillColor(...COLORS.emerald);
  doc.roundedRect(tableX + 98, totalY + 15, tableW - 98, 13, 4, 4, "F");

  doc.setTextColor(...COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Montant réglé", tableX + 103, totalY + 23.5);
  doc.text(formatEUR(total), tableX + tableW - 5, totalY + 23.5, {
    align: "right",
  });

  // Notes
  doc.setTextColor(...COLORS.slate500);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(club.taxNote, margin, totalY + 42);

  doc.text(
    "Cette facture atteste du règlement de la commande boutique indiquée ci-dessus.",
    margin,
    totalY + 48
  );

  drawFooter(doc);

  const fileName = `${invoiceNumber}-${cleanFileName(articleName || "commande")}.pdf`;
  doc.save(fileName);
};